import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { featured } from "@/mocks/featured";
import { getContentById, formatViews } from "@/utils/content";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const total = featured.length;

  const next = useCallback(() => setActive((v) => (v + 1) % total), [total]);
  const prev = useCallback(() => setActive((v) => (v - 1 + total) % total), [total]);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  const slide = featured[active];
  const item = getContentById(slide.contentId);

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      {featured.map((s, i) => (
        <div
          key={s.contentId}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === active ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={s.image}
            alt={`${getContentById(s.contentId)?.title ?? ""} 焦点推荐`}
            title={`${getContentById(s.contentId)?.title ?? ""} 国语对白高清影音`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/30" />
        </div>
      ))}

      <div className="relative z-10 h-full mx-auto max-w-page px-4 md:px-8 flex flex-col justify-end pb-12 md:pb-20">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
            <i className="ri-fire-line" />
            {slide.tag}
          </span>
          <h2 className="mt-4 text-2xl md:text-4xl font-bold text-background-50 font-heading leading-tight">
            {item?.title}
          </h2>
          <p className="mt-3 text-sm md:text-base text-background-200 leading-relaxed line-clamp-2 md:line-clamp-3">
            {item?.summary}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-background-200">
            <span>{item?.year}</span>
            <span>{item?.region}</span>
            <span className="flex items-center gap-1 text-accent-300">
              <i className="ri-star-fill" />
              {item?.rating}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {item ? formatViews(item.views) : ""}
            </span>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${slide.contentId}`}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-base" />
              查看详情
            </Link>
            <Link
              to="/category/movie"
              className="inline-flex items-center gap-1.5 rounded-md border border-background-50/40 bg-background-50/10 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-grid-line text-base" />
              分类浏览
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute z-20 bottom-5 right-4 md:right-8 flex items-center gap-3">
        <button
          type="button"
          onClick={prev}
          aria-label="上一张"
          className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full border border-background-50/40 bg-foreground-950/30 text-background-50 hover:bg-foreground-950/60 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>
        <button
          type="button"
          onClick={next}
          aria-label="下一张"
          className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full border border-background-50/40 bg-foreground-950/30 text-background-50 hover:bg-foreground-950/60 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>
      </div>

      <div className="absolute z-20 bottom-6 left-4 md:left-8 flex items-center gap-2">
        {featured.map((s, i) => (
          <button
            key={s.contentId}
            type="button"
            onClick={() => setActive(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === active ? "w-7 bg-accent-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}